import { classNames, sanitizeCustomProperties } from 'volta/utils/style-utils';
import { getResponsiveProps } from '@bevolta/design-tokens';
import Component from '@glimmer/component';
import element from 'ember-element-helper/helpers/element';
import orElse from 'volta/helpers/or-else';
import cssVars from 'volta/modifiers/css-vars';

import type { ResponsiveProp, SpaceScale } from '@bevolta/design-tokens';

type Align = 'start' | 'end' | 'center' | 'baseline';

type Element = 'div' | 'ul' | 'ol' | 'fieldset' | 'header' | 'footer' | 'section' | 'nav';

type Gap = ResponsiveProp<SpaceScale>;

export interface BvStackSignature {
  Args: {
    /** HTML Element type
     * @default 'div'
     */
    as?: Element;
    /** Horizontal alignment of children */
    align?: Align;
    /** The spacing between children */
    gap?: Gap;
    /** Reverse the render order of child items
     * @default false
     */
    reverseOrder?: boolean;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLElement;
}

export default class BvStackComponent extends Component<BvStackSignature> {
  get style() {
    const { align, gap, reverseOrder = false } = this.args;
    return sanitizeCustomProperties({
      '--bv-stack-align': align ? `${align}` : '',
      '--bv-stack-order': reverseOrder ? 'column-reverse' : 'column',
      ...getResponsiveProps('stack', 'gap', 'spacing', gap)
    }) as Record<string, string>;
  }

  get classnames() {
    const { as } = this.args;

    return classNames([
      'bv-stack',
      (as === 'ul' || as === 'ol') && 'bv-stack--list-reset',
      as === 'fieldset' && 'bv-stack--fieldset-reset'
    ]);
  }

  <template>
    {{#let (element (orElse @as 'div')) as |Element|}}
      <Element class={{this.classnames}} {{cssVars this.style}} ...attributes>
        {{yield}}
      </Element>
    {{/let}}
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvStack: typeof BvStackComponent;
    'bv-stack': typeof BvStackComponent;
  }
}
